import {
  CurrentUserType,
  useCurrentUserType,
} from "@/components/contexts/CurrentUserTypeContext";
import { useComebackUrl } from "@/components/hooks/use-comeback-url";
import { useToast } from "@/components/ui/use-toast";
import { appUrl } from "@/utils/app-url";
import { logger } from "@/utils/logger";
import { pagesPath } from "@/utils/pages-path";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type ErrorType =
  | "UNKNOWN"
  | "USER_NOT_FOUND"
  | "ALREADY_LOGGED_IN"
  | "CUSTOM_ERROR";

type ErrorHandlerOptions = {
  [key in ErrorType]?: {
    message: string | null;
    action: (params?: { message?: string | null }) => void;
  };
};

export const useErrorHandler = () => {
  const [error, setError] = useState<{
    type: ErrorType;
    customMessage?: string;
  } | null>(null);
  const { toast } = useToast();
  const lastToastRef = useRef<{
    type: ErrorType;
    time: number;
    customMessage?: string;
  } | null>(null);
  const { type: userType } = useCurrentUserType();
  const options = useErrorHandlerOptions({ userType });

  const handleError = useCallback(
    (errorType: ErrorType, customMessage?: string) => {
      setError({ type: errorType, customMessage });
    },
    [],
  );

  useEffect(() => {
    if (error) {
      const { type: errorType, customMessage } = error;
      const errorConfig = options[errorType] || {
        message: customMessage || errorType,
        action: () => logger.error(`Unhandled error: ${errorType}`),
      };

      const now = Date.now();
      const shouldShowToast =
        !lastToastRef.current ||
        lastToastRef.current.type !== errorType ||
        lastToastRef.current.customMessage !== customMessage ||
        now - lastToastRef.current.time > 1000; // 1秒以内に同じエラーのトーストを表示しない

      // カスタムメッセージがある場合は優先的に使用
      const displayMessage = customMessage || errorConfig.message;

      if (displayMessage && shouldShowToast) {
        toast({
          title: "エラー",
          description: displayMessage,
          variant: "error",
        });
        lastToastRef.current = {
          type: errorType,
          time: now,
          customMessage: customMessage,
        };
      }

      errorConfig.action({ message: customMessage });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(null);
    }
  }, [error, options, toast]);

  return handleError;
};

type ErrorHandlerOptionsParams = {
  userType?: CurrentUserType;
};

const useErrorHandlerOptions = (
  params?: ErrorHandlerOptionsParams,
): ErrorHandlerOptions => {
  const { getComebackUrl, setComebackUrl } = useComebackUrl();
  const pathname = usePathname();
  const router = useRouter();
  const loginUrl = useMemo(
    () =>
      params?.userType === "employer"
        ? pagesPath.employers.login.$url().path
        : params?.userType === "member"
          ? pagesPath.members.login.$url().path
          : pagesPath.admin.login.$url().path,
    [params?.userType],
  );

  return useMemo(
    () => ({
      ALREADY_LOGGED_IN: {
        message: null,
        action: () => {
          const defaultUrl = pagesPath.members.$url().path;
          router.push(getComebackUrl() ?? defaultUrl);
        },
      },
      USER_NOT_FOUND: {
        message: "ログインが必要です",
        action: () => {
          setComebackUrl(appUrl(pathname));
          router.push(loginUrl);
        },
      },
      CUSTOM_ERROR: {
        message: null, // カスタムエラーメッセージはhandleErrorで指定
        action: (params) =>
          logger.warn(
            `Custom error occurred: ${params?.message || "No message provided"}`,
          ),
      },
      UNKNOWN: {
        message: "不明なエラーが発生しました",
        action: () => logger.error("Unknown error occurred"),
      },
    }),
    [getComebackUrl, setComebackUrl, loginUrl, pathname, router],
  );
};
